/**
 * EventScopedDashboard — what an admin invited onto a single event sees.
 * Only their event(s) and the tools for them: no other events, no other
 * events' team or attendees, and none of the ecosystem modules.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, MapPin, Users, ArrowRight, ShieldCheck } from 'lucide-react';
import { eventAPI } from '@/lib/api';

export const EventScopedDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { data: events, isLoading } = useQuery({
    queryKey: ['admin-events'],
    queryFn: () => eventAPI.listForAdmin().then((r) => r.data),
  });

  const list = Array.isArray(events) ? events : [];
  const roleFor = (id) => (user?.event_roles || {})[id] || 'event_admin';

  return (
    <DashboardLayout title="Event Admin">
      <div className="space-y-6 max-w-5xl" data-testid="event-scoped-dashboard">
        <div>
          <h1 className="text-2xl font-bold">
            Welcome, {user?.first_name || user?.name || 'team member'}
          </h1>
          <p className="mt-1 text-slate-500">
            You have access to {list.length} event{list.length === 1 ? '' : 's'}.
          </p>
        </div>

        <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
             data-testid="event-scope-notice">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
          <p>
            Your admin access is scoped to your event. Other events, their teams and attendees,
            and the wider ecosystem modules are not part of your view. Ask a platform admin if you
            need more.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32" />)}</div>
        ) : list.length === 0 ? (
          <Card data-testid="event-scope-empty">
            <CardContent className="py-12 text-center text-slate-500">
              No event has been assigned to you yet. The organiser who invited you can add you to
              the event team.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {list.map((ev) => (
              <Card key={ev.id} data-testid={`scoped-event-${ev.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-lg font-semibold leading-snug">{ev.name}</h2>
                    <Badge variant="outline" className="shrink-0">
                      {String(roleFor(ev.id)).replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    {ev.start_date && (
                      <p className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        {new Date(ev.start_date).toLocaleDateString()}
                        {ev.end_date ? ` – ${new Date(ev.end_date).toLocaleDateString()}` : ''}
                      </p>
                    )}
                    {(ev.venue?.name || ev.venue_name) && (
                      <p className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {ev.venue?.name || ev.venue_name}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-slate-400" />
                      {ev.delegate_count ?? 0} registered
                    </p>
                  </div>
                  <Button className="mt-6 w-full" data-testid={`open-scoped-event-${ev.id}`}
                          onClick={() => navigate(`/admin/event/${ev.id}`)}>
                    Manage this event <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EventScopedDashboard;
